import { useState, useRef } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { AlertCircle, Zap, Save } from 'lucide-react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useSkillStore } from '../../../../stores/skillStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useItemStore } from '../../../../stores/itemStore'
import type { SkillInput, SkillResult } from '@creanizer/types'
import { SkillForm } from './SkillForm'
import { SkillFormStandard } from './SkillFormStandard'
import { SkillFormIdea } from './SkillFormIdea'
import { SkillFormBatch } from './SkillFormBatch'
import { SkillFormEvolution } from './SkillFormEvolution'
import { SkillFormCombination } from './SkillFormCombination'
import { SkillResult as SkillResultComponent } from './SkillResult'
import { SavedSkillsList } from './SavedSkillsList'

export function SkillGenerator() {
  const { isApiKeySet } = useSettingsStore()
  const { skills, addSkill, addBatchSkills } = useSkillStore()
  const { currentWorld, savedWorlds } = useWorldBuilderStore()
  const { characters } = useCharacterStore()
  const { items } = useItemStore()
  const generator = useGenerator('skill')
  const streamGenerator = useStreamGenerator('skill')
  const [activeTab, setActiveTab] = useState('generate')
  const [selectedWorldId, setSelectedWorldId] = useState<string>('')
  const [selectedCharacterId, setSelectedCharacterId] = useState<string>('')
  const [selectedItemId, setSelectedItemId] = useState<string>('')
  const [selectedEnemyId, setSelectedEnemyId] = useState<string>('')
  const [selectedBaseSkillId, setSelectedBaseSkillId] = useState<string>('')
  const [selectedCombinationSkills, setSelectedCombinationSkills] = useState<string[]>([])
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  
  const [input, setInput] = useState<SkillInput>({
    mode: 'standard',
    skillType: 'active',
    tier: 'basic',
    rarity: 'common',
    level: undefined,
    genre: 'fantasy',
    powerLevel: 5,
    complexity: 5,
    masteryDifficulty: 5,
    visualStyle: 'realistic',
    creativityLevel: 7,
    numberOfSkills: 5,
    batchPattern: 'random',
    includeVariations: false,
    includeCounterSkills: false,
    worldContext: currentWorld ? JSON.stringify({
      name: currentWorld.result.name,
      parameters: currentWorld.result.parameters,
      genres: currentWorld.result.genres
    }) : undefined
  })
  
  const [results, setResults] = useState<SkillResult[]>([])
  const [error, setError] = useState<string>('')
  const [batchProgress, setBatchProgress] = useState<{
    current: number;
    total: number;
    chunks?: { index: number; status: 'pending' | 'processing' | 'success' | 'failed'; count: number }[]
  } | null>(null)
  const resultRef = useRef<HTMLDivElement>(null)
  
  const handleGenerate = async () => {
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません。設定ページから設定してください。')
      return
    }
    
    setError('')
    setResults([])
    setBatchProgress(null)
    
    try {
      // バッチモードで5個以上の場合はチャンク処理
      if (input.mode === 'batch' && (input.numberOfSkills || 5) > 5) {
        const totalSkills = input.numberOfSkills || 5
        const chunkSize = 5
        const chunks = Math.ceil(totalSkills / chunkSize)
        const allResults: SkillResult[] = []

        // チャンクの初期状態を設定
        const chunkStatuses: { index: number; status: 'pending' | 'processing' | 'success' | 'failed'; count: number }[] = Array.from({ length: chunks }, (_, i) => ({
          index: i,
          status: 'pending',
          count: Math.min(chunkSize, totalSkills - (i * chunkSize))
        }))

        for (let i = 0; i < chunks; i++) {
          const currentChunkSize = Math.min(chunkSize, totalSkills - (i * chunkSize))
          const currentChunkInput = {
            ...input,
            numberOfSkills: currentChunkSize
          }

          // チャンクステータスを更新
          chunkStatuses[i].status = 'processing'
          setBatchProgress({
            current: i * chunkSize + 1,
            total: totalSkills,
            chunks: [...chunkStatuses]
          })
          setError(`生成中... (${i * chunkSize + 1}-${Math.min((i + 1) * chunkSize, totalSkills)}/${totalSkills}個)`)
          
          try {
            if (useStreaming) {
              await new Promise<void>((resolve, reject) => {
                streamGenerator.generateStream(currentChunkInput, {
                  onChunk: (chunk) => {
                    console.log(`Chunk ${i + 1}/${chunks} - Received:`, chunk.length)
                  },
                  onProgress: (bytes) => {
                    console.log(`Chunk ${i + 1}/${chunks} - Progress:`, bytes)
                  },
                  onComplete: (resultData) => {
                    if (resultData !== null && resultData !== undefined) {
                      const rawResults = Array.isArray(resultData) ? resultData : [resultData]
                      const validResults = rawResults.filter(skill => 
                        skill && skill.basicInfo && skill.basicInfo.name
                      )
                      
                      if (validResults.length > 0) {
                        allResults.push(...(validResults as SkillResult[]))
                        setResults([...allResults])
                        // 成功ステータスに更新
                        chunkStatuses[i].status = 'success'
                        setBatchProgress({
                          current: Math.min((i + 1) * chunkSize, totalSkills),
                          total: totalSkills,
                          chunks: [...chunkStatuses]
                        })
                      }
                      resolve()
                    } else {
                      reject(new Error('チャンクの生成に失敗しました'))
                    }
                  },
                  onError: (error) => {
                    console.error(`Chunk ${i + 1}/${chunks} - Error:`, error)
                    reject(error)
                  }
                })
              })
            } else {
              const response = await generator.mutateAsync(currentChunkInput)
              const resultData = response.result || response
              
              if (resultData) {
                const rawResults = Array.isArray(resultData) ? resultData : [resultData]
                const validResults = rawResults.filter(skill => 
                  skill && skill.basicInfo && skill.basicInfo.name
                )
                
                if (validResults.length > 0) {
                  allResults.push(...validResults)
                  setResults([...allResults])
                  // 成功ステータスに更新
                  chunkStatuses[i].status = 'success'
                  setBatchProgress({
                    current: Math.min((i + 1) * chunkSize, totalSkills),
                    total: totalSkills,
                    chunks: [...chunkStatuses]
                  })
                }
              }
            }
            
            // 次のチャンクまで少し待機（APIレート制限対策）
            if (i < chunks - 1) {
              await new Promise(resolve => setTimeout(resolve, 1000))
            }
          } catch (chunkError) {
            console.error(`Chunk ${i + 1} failed:`, chunkError)

            // リトライ機構
            const maxRetries = 2
            let retryCount = 0
            let retrySuccess = false

            while (retryCount < maxRetries && !retrySuccess) {
              retryCount++
              console.log(`Retrying chunk ${i + 1} (attempt ${retryCount}/${maxRetries})...`)
              setError(`チャンク ${i + 1} の再試行中... (${retryCount}/${maxRetries}) - 1分待機中...`)

              // 1分待機してからリトライ
              await new Promise(resolve => setTimeout(resolve, 60000))

              try {
                if (useStreaming) {
                  await new Promise<void>((resolve, reject) => {
                    streamGenerator.generateStream(currentChunkInput, {
                      onChunk: (chunk) => {
                        console.log(`Chunk ${i + 1} retry ${retryCount} - Received:`, chunk.length)
                      },
                      onProgress: (bytes) => {
                        console.log(`Chunk ${i + 1} retry ${retryCount} - Progress:`, bytes)
                      },
                      onComplete: (resultData) => {
                        if (resultData !== null && resultData !== undefined) {
                          const rawResults = Array.isArray(resultData) ? resultData : [resultData]
                          const validResults = rawResults.filter(skill =>
                            skill && skill.basicInfo && skill.basicInfo.name
                          )

                          if (validResults.length > 0) {
                            allResults.push(...(validResults as SkillResult[]))
                            setResults([...allResults])
                            retrySuccess = true
                            // リトライ成功時のステータス更新
                            chunkStatuses[i].status = 'success'
                            setBatchProgress({
                              current: Math.min((i + 1) * chunkSize, totalSkills),
                              total: totalSkills,
                              chunks: [...chunkStatuses]
                            })
                          }
                          resolve()
                        } else {
                          reject(new Error('リトライでもチャンクの生成に失敗しました'))
                        }
                      },
                      onError: (error) => {
                        console.error(`Chunk ${i + 1} retry ${retryCount} - Error:`, error)
                        reject(error)
                      }
                    })
                  })
                } else {
                  const response = await generator.mutateAsync(currentChunkInput)
                  const resultData = response.result || response

                  if (resultData) {
                    const rawResults = Array.isArray(resultData) ? resultData : [resultData]
                    const validResults = rawResults.filter(skill =>
                      skill && skill.basicInfo && skill.basicInfo.name
                    )

                    if (validResults.length > 0) {
                      allResults.push(...validResults)
                      setResults([...allResults])
                      retrySuccess = true
                      // リトライ成功時のステータス更新
                      chunkStatuses[i].status = 'success'
                      setBatchProgress({
                        current: Math.min((i + 1) * chunkSize, totalSkills),
                        total: totalSkills,
                        chunks: [...chunkStatuses]
                      })
                    }
                  }
                }

                if (retrySuccess) {
                  console.log(`Chunk ${i + 1} succeeded on retry ${retryCount}`)
                  setError(`生成中... (${Math.min((i + 1) * chunkSize, totalSkills)}/${totalSkills}個)`)
                }
              } catch (retryError) {
                console.error(`Chunk ${i + 1} retry ${retryCount} failed:`, retryError)
              }
            }

            // リトライも失敗した場合、次のチャンクに進む
            if (!retrySuccess) {
              console.warn(`Chunk ${i + 1} failed after ${maxRetries} retries, continuing with next chunk...`)
              setError(`チャンク ${i + 1} の生成をスキップしています...`)
              // 失敗ステータスに更新
              chunkStatuses[i].status = 'failed'
              setBatchProgress({
                current: i * chunkSize + currentChunkSize,
                total: totalSkills,
                chunks: [...chunkStatuses]
              })
              // 少し待ってから次のチャンクへ
              await new Promise(resolve => setTimeout(resolve, 3000))
            }
          }
        }
        
        setBatchProgress(null)
        if (allResults.length > 0) {
          // 完了メッセージの設定
          if (allResults.length === totalSkills) {
            setError('') // すべて成功
          } else {
            // 部分的な成功を通知
            const failedCount = totalSkills - allResults.length
            setError(`${allResults.length}個のスキルが生成されました（${failedCount}個の生成に失敗）`)
          }
          setTimeout(() => {
            resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }, 100)
        } else {
          setError('すべてのスキルの生成に失敗しました。APIキーの確認や生成数を減らしてお試しください。')
        }

        return
      }
      
      // 通常の処理（5個以下または単体生成）
      if (useStreaming) {
        await streamGenerator.generateStream(input, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (bytes) => {
            console.log('Progress:', bytes)
          },
          onComplete: (resultData) => {
            if (resultData !== null && resultData !== undefined) {
              if (Array.isArray(resultData) && resultData.length === 0) {
                console.error('No skills could be recovered from incomplete data')
                setError('データの取得に失敗しました。生成数を減らすか、再度お試しください。')
                return
              }
              
              const rawResults = Array.isArray(resultData) ? resultData : [resultData]
              const validResults = rawResults.filter(skill => 
                skill && skill.basicInfo && skill.basicInfo.name
              )
              
              if (validResults.length === 0) {
                console.error('No valid skills in results')
                setError('有効なスキルデータが取得できませんでした。')
                return
              }
              
              setResults(validResults as SkillResult[])
              
              if (input.mode === 'batch' && Array.isArray(resultData)) {
                const expectedCount = input.numberOfSkills || 5
                if (validResults.length > 0 && validResults.length < expectedCount) {
                  console.warn(`Partial recovery: ${validResults.length}/${expectedCount} skills recovered`)
                  setError(`${validResults.length}個のみ取得できました（${expectedCount}個中）`)
                }
              }
              
              setTimeout(() => {
                resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }, 100)
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message)
          }
        })
      } else {
        const response = await generator.mutateAsync(input)
        const resultData = response.result || response
        
        if (!resultData) {
          throw new Error('生成結果が空です。APIが混雑している可能性があります。')
        }
        
        const rawResults = Array.isArray(resultData) ? resultData : [resultData]
        const validResults = rawResults.filter(skill => 
          skill && skill.basicInfo && skill.basicInfo.name
        )
        
        if (validResults.length === 0) {
          throw new Error('有効なスキルデータが取得できませんでした。')
        }
        
        setResults(validResults as SkillResult[])
        
        setTimeout(() => {
          resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    } catch (err: any) {
      console.error('Generation error:', err)
      setError(err.message || '生成中にエラーが発生しました')
    }
  }
  
  const handleSave = () => {
    if (results.length === 0) return
    
    if (input.mode === 'batch') {
      addBatchSkills(results.map(result => ({
        input,
        result
      })))
    } else {
      results.forEach(result => {
        addSkill({ input, result })
      })
    }
  }
  
  const handleLoadSaved = (skill: { input: SkillInput; result: SkillResult }) => {
    setInput(skill.input)
    setResults([skill.result])
    setActiveTab('generate')
  }
  
  const handleModeChange = (newMode: string) => {
    // 共通設定は保持（世界観、キャラクター、エネミー、アイテム、生成オプション）
    const commonFields = {
      genre: input.genre,
      customGenre: input.customGenre,
      worldContext: input.worldContext,
      relatedCharacterContext: input.relatedCharacterContext,
      characterClass: input.characterClass,
      relatedItemContext: input.relatedItemContext,
      relatedEnemyContext: input.relatedEnemyContext,
      enemyId: input.enemyId,
      useProMode: input.useProMode,
      creativityLevel: input.creativityLevel,
      includeVariations: input.includeVariations,
      includeCounterSkills: input.includeCounterSkills
    }
    
    switch (newMode) {
      case 'standard':
        setInput({
          ...commonFields,
          mode: 'standard' as const,
          // 標準モード固有のフィールド
          name: '',
          skillType: 'active',
          customType: '',
          category: '',
          tier: 'basic',
          rarity: 'common',
          level: undefined,
          powerLevel: 5,
          complexity: 5,
          masteryDifficulty: 5,
          visualStyle: 'realistic',
          elementalAffinity: '',
          additionalSettings: '',
          // 他モードのフィールドを明示的にクリア
          ideaText: undefined,
          uploadedContent: undefined,
          baseSkillContext: undefined,
          evolutionPath: undefined,
          combinationSkills: undefined,
          numberOfSkills: undefined,
          batchTheme: undefined,
          batchPattern: undefined,
          progressionType: undefined,
          relatedSkillContext: undefined
        })
        break
        
      case 'idea':
        setInput({
          ...commonFields,
          mode: 'idea' as const,
          // アイディアモード固有のフィールド
          ideaText: '',
          uploadedContent: undefined,
          // 標準モードのフィールドを明示的にundefined（AIに判断を任せる）
          name: undefined,
          skillType: undefined,
          customType: undefined,
          category: undefined,
          tier: undefined,
          rarity: undefined,
          level: undefined,
          powerLevel: undefined,
          complexity: undefined,
          masteryDifficulty: undefined,
          visualStyle: undefined,
          elementalAffinity: undefined,
          additionalSettings: undefined,
          // 他モードのフィールドをクリア
          baseSkillContext: undefined,
          evolutionPath: undefined,
          combinationSkills: undefined,
          numberOfSkills: undefined,
          batchTheme: undefined,
          batchPattern: undefined,
          progressionType: undefined,
          relatedSkillContext: undefined,
          evolutionStage: undefined
        })
        break
        
      case 'batch':
        setInput({
          ...commonFields,
          mode: 'batch' as const,
          // バッチモード固有のフィールド
          numberOfSkills: 5,
          batchTheme: '',
          batchPattern: 'random',
          progressionType: undefined,
          // 標準モードのフィールドを明示的にundefined（AIに判断を任せる）
          name: undefined,
          skillType: undefined,
          customType: undefined,
          category: undefined,
          tier: undefined,
          rarity: undefined,
          level: undefined,
          powerLevel: undefined,
          complexity: undefined,
          masteryDifficulty: undefined,
          visualStyle: undefined,
          elementalAffinity: undefined,
          additionalSettings: undefined,
          // 他モードのフィールドをクリア
          ideaText: undefined,
          uploadedContent: undefined,
          baseSkillContext: undefined,
          evolutionPath: undefined,
          combinationSkills: undefined,
          relatedSkillContext: undefined,
          evolutionStage: undefined
        })
        break
        
      case 'evolution':
        setInput({
          ...commonFields,
          mode: 'evolution' as const,
          // 進化モード固有のフィールド
          evolutionPath: 'power',
          baseSkillContext: undefined,
          // 標準モードのフィールドを明示的にundefined（ベーススキルから派生）
          name: undefined,
          skillType: undefined,
          customType: undefined,
          category: undefined,
          tier: undefined,
          rarity: undefined,
          level: undefined,
          powerLevel: undefined,
          complexity: undefined,
          masteryDifficulty: undefined,
          visualStyle: undefined,
          elementalAffinity: undefined,
          additionalSettings: undefined,
          // 他モードのフィールドをクリア
          ideaText: undefined,
          uploadedContent: undefined,
          combinationSkills: undefined,
          numberOfSkills: undefined,
          batchTheme: undefined,
          batchPattern: undefined,
          progressionType: undefined,
          relatedSkillContext: undefined,
          evolutionStage: undefined
        })
        setSelectedBaseSkillId('')
        break
        
      case 'combination':
        setInput({
          ...commonFields,
          mode: 'combination' as const,
          // 組み合わせモード固有のフィールド
          combinationSkills: [],
          // 標準モードのフィールドを明示的にundefined（組み合わせから生成）
          name: undefined,
          skillType: undefined,
          customType: undefined,
          category: undefined,
          tier: undefined,
          rarity: undefined,
          level: undefined,
          powerLevel: undefined,
          complexity: undefined,
          masteryDifficulty: undefined,
          visualStyle: undefined,
          elementalAffinity: undefined,
          additionalSettings: undefined,
          // 他モードのフィールドをクリア
          ideaText: undefined,
          uploadedContent: undefined,
          baseSkillContext: undefined,
          evolutionPath: undefined,
          numberOfSkills: undefined,
          batchTheme: undefined,
          batchPattern: undefined,
          progressionType: undefined,
          relatedSkillContext: undefined,
          evolutionStage: undefined
        })
        setSelectedCombinationSkills([])
        break
    }
  }
  
  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="h-5 w-5" />
          <h2 className="text-xl font-semibold">スキルジェネレータ</h2>
        </div>
        <p className="text-muted-foreground mb-4">
          キャラクターの技能・魔法・必殺技を詳細に生成します
        </p>
      </div>
        
        {!isApiKeySet() && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              APIキーが設定されていません。設定ページから設定してください。
            </AlertDescription>
          </Alert>
        )}
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="generate">生成</TabsTrigger>
            <TabsTrigger value="saved">保存済み ({skills.length}/500)</TabsTrigger>
          </TabsList>
          
          <TabsContent value="generate" className="space-y-6">
            <div className="card">
              <h3 className="text-lg font-semibold mb-4">生成設定</h3>
              <SkillForm
              input={input}
              onChange={setInput}
              onModeChange={handleModeChange}
              worlds={savedWorlds}
              characters={characters}
              items={items}
              savedSkills={skills}
              selectedWorldId={selectedWorldId}
              setSelectedWorldId={setSelectedWorldId}
              selectedCharacterId={selectedCharacterId}
              setSelectedCharacterId={setSelectedCharacterId}
              selectedItemId={selectedItemId}
              setSelectedItemId={setSelectedItemId}
              selectedEnemyId={selectedEnemyId}
              setSelectedEnemyId={setSelectedEnemyId}
              selectedBaseSkillId={selectedBaseSkillId}
              setSelectedBaseSkillId={setSelectedBaseSkillId}
              selectedCombinationSkills={selectedCombinationSkills}
              setSelectedCombinationSkills={setSelectedCombinationSkills}
              useStreaming={useStreaming}
              setUseStreaming={setUseStreaming}
            />
            
            <div className="flex justify-end mt-4">
              <Button
                onClick={handleGenerate}
                disabled={generator.isPending || streamGenerator.isStreaming}
              >
                {generator.isPending || streamGenerator.isStreaming ? '生成中...' : 'スキルを生成'}
              </Button>
              
              </div>
            </div>
            
            {(streamGenerator.isStreaming && streamGenerator.progress > 0) || batchProgress ? (
              <div className="card">
                <div className="space-y-2">
                <Progress value={streamGenerator.progress} />
                <p className="text-sm text-muted-foreground text-center">
                  生成中... {Math.round(streamGenerator.progress)}%
                </p>
                {batchProgress && (
                  <div className="mt-2 space-y-2">
                    <p className="text-sm font-medium text-center">
                      進捗: {batchProgress.current}/{batchProgress.total}個
                    </p>
                    {batchProgress.chunks && batchProgress.chunks.length > 1 && (
                      <div className="flex gap-1 justify-center flex-wrap">
                        {batchProgress.chunks.map((chunk, idx) => (
                          <div
                            key={idx}
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              chunk.status === 'success'
                                ? 'bg-green-500/20 text-green-500'
                                : chunk.status === 'failed'
                                ? 'bg-red-500/20 text-red-500'
                                : chunk.status === 'processing'
                                ? 'bg-blue-500/20 text-blue-500 animate-pulse'
                                : 'bg-gray-500/20 text-gray-500'
                            }`}
                          >
                            チャンク{idx + 1} ({chunk.count}個)
                            {chunk.status === 'processing' && ' ⏳'}
                            {chunk.status === 'success' && ' ✓'}
                            {chunk.status === 'failed' && ' ✗'}
                          </div>
                        ))}
                      </div>
                    )}
                    {(input.numberOfSkills || 5) > 5 && (
                      <p className="text-xs text-muted-foreground text-center">
                        ※ {Math.ceil((input.numberOfSkills || 5) / 5)}回のAPIを使用して生成します
                      </p>
                    )}
                  </div>
                )}
                {streamGenerator.partialContent && (
                  <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {streamGenerator.partialContent.slice(-500)}
                      {streamGenerator.partialContent.length > 500 && '...'}
                    </pre>
                  </div>
                )}
                </div>
              </div>
            ) : null}
            
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {results.length > 0 && (
              <div className="card" ref={resultRef}>
                <div className="flex flex-row items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    生成結果
                    {results.length > 1 && ` (${results.length}個)`}
                  </h3>
                  {results.length > 1 && (
                    <Button
                      onClick={handleSave}
                      variant="outline"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      すべて保存
                    </Button>
                  )}
                </div>
                {results.map((result, index) => (
                  <SkillResultComponent
                    key={index}
                    result={result}
                    onSave={() => {
                      addSkill({ input, result })
                    }}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="saved">
            <SavedSkillsList onLoad={handleLoadSaved} />
          </TabsContent>
        </Tabs>
    </div>
  )
}